/**
 * Scenario loading in the browser.
 *
 * Same files, same validator as the Node CLI — `import.meta.glob` inlines the JSON at
 * build time, so a scenario that loads under `npm run scenarios` loads here. Files
 * starting with `_` are skipped: `_schema.json` is tooling, not a scenario.
 */

import { parseScenario, type Scenario } from 'walkout-engine';

const modules = import.meta.glob<{ default: unknown }>('../../../../data/scenarios/*.json', {
  eager: true,
});

function loadAll(): Scenario[] {
  const scenarios: Scenario[] = [];
  for (const [path, module] of Object.entries(modules)) {
    const filename = path.split('/').pop() ?? path;
    if (filename.startsWith('_')) continue;
    scenarios.push(parseScenario(module.default, filename));
  }
  return scenarios.sort((a, b) => a.name.localeCompare(b.name));
}

export const SCENARIOS: Scenario[] = loadAll();

export function scenarioById(id: string): Scenario | undefined {
  return SCENARIOS.find((s) => s.id === id);
}

/**
 * The scenario this page is about.
 *
 * The build emits a small landing page per scenario at `/s/<id>/` so that shared links
 * preview as themselves rather than as one generic card — a hash never reaches a server,
 * so the scenario cannot be read from the URL. Those pages carry a `walkout-scenario` meta
 * tag, and this is where it is honoured. On the root page there is no tag and the first
 * scenario is used.
 */
function scenarioFromPage(): Scenario | undefined {
  if (typeof document === 'undefined') return undefined;
  const tag = document.querySelector('meta[name="walkout-scenario"]');
  const id = tag?.getAttribute('content');
  return id ? scenarioById(id) : undefined;
}

export const DEFAULT_SCENARIO: Scenario = scenarioFromPage() ?? (SCENARIOS[0] as Scenario);
